/**
 * noteConflictScanner — Pre-generate input scanner (Feb 26, 2026)
 *
 * Detects common patterns that cause downstream LLM hallucinations or
 * unsignable notes:
 *
 *   1. PRONOUN MIX — same note refers to the patient as both "he" and "she"
 *      (or "him" + "her") inside the same sentence/paragraph. This usually
 *      means the provider's voice transcription picked up text from a
 *      PREVIOUS patient's note, or there's a leftover boilerplate snippet.
 *      The downstream prompt enforces gender from the demographic header,
 *      but a mid-note pronoun flip is still a tell that the input is dirty.
 *
 *   2. ENDORSE / DENIES CONTRADICTION — same symptom appears in both an
 *      "endorses" / "complains of" / "reports" clause AND a "denies" clause
 *      in the same input. Example:
 *          "Patient endorses cough x 3 days. Denies cough, fever, SOB."
 *      The LLM will either silently drop one OR emit both as contradictory
 *      A/P bullets. Surface it BEFORE generate so the provider can pick one.
 *
 * Returns an array of conflict objects:
 *   { id, kind, severity, title, detail, suggestion }
 *
 * `severity` is "high" for endorse/denies on the same symptom (clinical risk)
 * and "medium" for pronoun mix (formatting risk). Empty array = no conflicts.
 */

const PRONOUNS_MALE = ['he', 'him', 'his', 'himself'];
const PRONOUNS_FEMALE = ['she', 'her', 'hers', 'herself'];

// Symptoms / findings that commonly appear in endorse↔denies whipsaws.
// Multi-word entries match the full phrase. Keep this list focused on
// high-frequency PCM complaints; the scanner is meant to surface obvious
// contradictions, not be a full medical lexicon.
const SYMPTOMS = [
  'cough', 'fever', 'chills', 'sweats', 'night sweats',
  'shortness of breath', 'sob', 'dyspnea',
  'chest pain', 'chest pressure', 'palpitations',
  'headache', 'dizziness', 'syncope', 'lightheadedness',
  'nausea', 'vomiting', 'diarrhea', 'constipation', 'abdominal pain',
  'sore throat', 'congestion', 'rhinorrhea', 'ear pain',
  'fatigue', 'weakness', 'malaise',
  'back pain', 'neck pain', 'joint pain', 'knee pain', 'shoulder pain',
  'numbness', 'tingling', 'paresthesia', 'weakness',
  'rash', 'pruritus', 'itching',
  'dysuria', 'hematuria', 'frequency', 'urgency',
  'anxiety', 'depression', 'insomnia', 'suicidal ideation', 'si',
  'homicidal ideation', 'hi',
  'tobacco', 'alcohol', 'illicit drugs',
  'loc', 'loss of consciousness',
  'visual changes', 'blurred vision', 'photophobia',
  'tinnitus', 'hearing loss',
];

// Word-boundary tokenizer that's tolerant of punctuation.
const tokenize = (text) => (text || '').toLowerCase().split(/[^a-z]+/).filter(Boolean);

const sentenceSplit = (text) => {
  if (!text) return [];
  // Split on . ? ! or hard line breaks. Keep clauses around "but"/"however"/"although"
  // glued because providers often write "denies X but endorses Y" — that's a same-clause
  // claim and should NOT trip the contradiction scan.
  return text
    .split(/(?<=[.?!])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

// Detect pronoun mix anywhere in the input. Returns null or a conflict object.
const detectPronounMix = (text) => {
  const tokens = tokenize(text);
  const tokenSet = new Set(tokens);
  const maleHits = PRONOUNS_MALE.filter((p) => tokenSet.has(p));
  const femaleHits = PRONOUNS_FEMALE.filter((p) => tokenSet.has(p));
  if (maleHits.length === 0 || femaleHits.length === 0) return null;

  // Find an example sentence containing both genders for the modal preview.
  const sentences = sentenceSplit(text);
  const example = sentences.find((s) => {
    const toks = new Set(tokenize(s));
    const hasM = PRONOUNS_MALE.some((p) => toks.has(p));
    const hasF = PRONOUNS_FEMALE.some((p) => toks.has(p));
    return hasM && hasF;
  });

  return {
    id: 'pronoun-mix',
    kind: 'pronoun',
    severity: 'medium',
    title: 'Pronoun mix detected',
    detail: example
      ? `Both male and female pronouns appear in the note. Example: \u201C${example.slice(0, 220)}\u201D`
      : `Male pronouns (${maleHits.join(', ')}) and female pronouns (${femaleHits.join(', ')}) both appear in the note.`,
    suggestion:
      'Confirm the correct gender for this patient and remove the wrong-pronoun text. Mixed pronouns may indicate leftover transcript from a previous patient.',
    // Interactive fix payload (Feb 26, 2026) — modal renders these as buttons.
    // The scanner doesn't know WHICH gender is correct; it offers both and the
    // provider taps one. `applyPronounFix(notes, choice)` rewrites the wrong
    // pronouns in-place.
    fixActions: [
      { id: 'pronoun-male', label: 'Patient is Male', kind: 'pronoun_fix', payload: 'male' },
      { id: 'pronoun-female', label: 'Patient is Female', kind: 'pronoun_fix', payload: 'female' },
    ],
  };
};

// Detect a symptom that appears in BOTH an endorse/reports/complains-of context
// AND a denies context, across different sentences. Returns an array (could be
// multiple symptoms whipsawed).
const detectEndorseDeniesConflicts = (text) => {
  const sentences = sentenceSplit(text);
  if (sentences.length === 0) return [];

  // Classify each sentence as: positive (endorses), negative (denies), or neutral.
  // We tag the matching symptoms per sentence.
  const POS_RE = /\b(endorses|endorse|reports|reporting|complains? of|c\/o|states)\b/i;
  const NEG_RE = /\b(denies|denied|denying|negative for|no\s+(?:complaints? of|hx of)?)\b/i;

  const posSymptomsBySent = [];
  const negSymptomsBySent = [];

  for (const sent of sentences) {
    const lower = sent.toLowerCase();
    const isPos = POS_RE.test(sent);
    const isNeg = NEG_RE.test(sent);
    if (!isPos && !isNeg) continue;

    const found = [];
    for (const sym of SYMPTOMS) {
      const re = new RegExp(`\\b${sym.replace(/\s+/g, '\\s+')}\\b`, 'i');
      if (re.test(lower)) found.push(sym);
    }
    if (found.length === 0) continue;
    if (isPos && !isNeg) posSymptomsBySent.push({ sent, syms: found });
    else if (isNeg && !isPos) negSymptomsBySent.push({ sent, syms: found });
    // Mixed-in-same-sentence (e.g., "denies cough but endorses fever") is OK — skip.
  }

  // Build a set of symptoms that appear in BOTH lists.
  const posSet = new Set(posSymptomsBySent.flatMap((s) => s.syms));
  const negSet = new Set(negSymptomsBySent.flatMap((s) => s.syms));
  const conflicting = [...posSet].filter((s) => negSet.has(s));
  if (conflicting.length === 0) return [];

  return conflicting.map((sym) => {
    const posEx = posSymptomsBySent.find((s) => s.syms.includes(sym))?.sent || '';
    const negEx = negSymptomsBySent.find((s) => s.syms.includes(sym))?.sent || '';
    const symLabel = sym.toUpperCase();
    return {
      id: `endorse-denies-${sym.replace(/\s+/g, '-')}`,
      kind: 'endorse_denies',
      severity: 'high',
      title: `"${symLabel}" appears as both endorsed and denied`,
      detail: `Positive: \u201C${posEx.slice(0, 180)}\u201D\nNegative: \u201C${negEx.slice(0, 180)}\u201D`,
      suggestion:
        `Pick one. Either delete the "denies ${sym}" clause OR delete the "endorses/reports ${sym}" clause so the note reflects today's encounter accurately.`,
      // Interactive fix payload (Feb 26, 2026)
      fixActions: [
        { id: `keep-pos-${sym}`, label: `Keep "Endorses ${sym}"`, kind: 'endorse_denies_fix', payload: { symptom: sym, keep: 'positive' } },
        { id: `keep-neg-${sym}`, label: `Keep "Denies ${sym}"`,   kind: 'endorse_denies_fix', payload: { symptom: sym, keep: 'negative' } },
      ],
    };
  });
};

/**
 * scanNotesForConflicts — main entrypoint. Returns array of conflict objects.
 * Empty array = clean input, safe to generate.
 */
export const scanNotesForConflicts = (notes) => {
  if (!notes || typeof notes !== 'string' || notes.trim().length < 20) return [];
  const conflicts = [];
  const pron = detectPronounMix(notes);
  if (pron) conflicts.push(pron);
  conflicts.push(...detectEndorseDeniesConflicts(notes));
  return conflicts;
};

// =====================================================================
// SOFT WARNINGS — non-blocking advisory flags (Feb 26, 2026)
// =====================================================================
// Lower-confidence catches that should NOT block Generate but help the
// provider spot easy fixes. Rendered as a tiny inline chip below the
// textarea, NOT a modal. Each warning: { id, label, detail }.
// =====================================================================

// Common medication tokens — used to flag a dose without a unit. Kept short
// to avoid false positives; expand as the provider reports misses.
const MED_TOKENS = [
  'metformin', 'lisinopril', 'atorvastatin', 'amlodipine', 'losartan',
  'hydrochlorothiazide', 'hctz', 'omeprazole', 'pantoprazole', 'famotidine',
  'sertraline', 'fluoxetine', 'escitalopram', 'bupropion', 'trazodone',
  'gabapentin', 'pregabalin', 'duloxetine', 'venlafaxine',
  'levothyroxine', 'synthroid', 'metoprolol', 'carvedilol', 'propranolol',
  'cetirizine', 'zyrtec', 'loratadine', 'claritin', 'fexofenadine', 'allegra',
  'fluticasone', 'flonase', 'albuterol', 'montelukast', 'singulair',
  'ibuprofen', 'naproxen', 'meloxicam', 'celecoxib', 'acetaminophen', 'tylenol',
  'tramadol', 'oxycodone', 'hydrocodone', 'morphine',
  'amoxicillin', 'azithromycin', 'doxycycline', 'cephalexin', 'ciprofloxacin',
  'prednisone', 'methylprednisolone',
  'insulin', 'glipizide', 'empagliflozin', 'jardiance', 'semaglutide', 'ozempic',
];

// Detect "<med> <number>" without a following unit. Example: "metformin 500"
// (missing "mg") flags. "metformin 500 mg" passes.
const detectDoseWithoutUnit = (text) => {
  if (!text) return [];
  const flagged = [];
  const lower = text.toLowerCase();
  for (const med of MED_TOKENS) {
    // <med>\b\s+\d+(\.\d+)? followed by whitespace/punct/EOF, then NOT a unit.
    // The `(?=\s|$|[.,;:!?)])` anchor prevents regex backtracking from
    // accepting a partial digit match (e.g., "metformin 500 mg" trying 50).
    const re = new RegExp(
      `\\b${med}\\b\\s+(\\d+(?:\\.\\d+)?)(?=\\s|$|[.,;:!?)])(?!\\s*(?:mg|mcg|g\\b|ml|units?|iu|tab|tablet|cap))`,
      'gi'
    );
    let m;
    while ((m = re.exec(lower)) !== null) {
      const dose = m[1];
      flagged.push({
        id: `dose-no-unit-${med}-${dose}`,
        label: `${med} ${dose} — missing unit`,
        detail: `\u201C${med} ${dose}\u201D appears without a unit (mg, mcg, mL, units). Common cause of pharmacy clarification calls.`,
      });
      // Only flag the first hit per med to avoid noise on repeated mentions
      break;
    }
  }
  return flagged;
};

// Detect "follow up in _" / "f/u in _" / "RTC in _" placeholders that the
// provider intended to fill in but left blank. Common dictation residue.
const detectUnfilledFollowup = (text) => {
  if (!text) return [];
  const re = /\b(?:follow[-\s]?up|f\/u|rtc|return to clinic)\s+in\s+_+\b/i;
  if (!re.test(text)) return [];
  return [{
    id: 'unfilled-followup-window',
    label: 'Follow-up window left blank',
    detail: 'Found a "follow up in _" / "f/u in _" placeholder. Replace the underscore with a timeframe (e.g., "2 weeks", "1 month") so the LLM doesn\u2019t guess.',
  }];
};

/**
 * scanNotesForSoftWarnings — non-blocking flags. Returns array of warnings.
 * Empty array = nothing to flag.
 */
export const scanNotesForSoftWarnings = (notes) => {
  if (!notes || typeof notes !== 'string' || notes.trim().length < 10) return [];
  const out = [];
  out.push(...detectDoseWithoutUnit(notes));
  out.push(...detectUnfilledFollowup(notes));
  return out;
};

// =====================================================================
// AUTO-FIX APPLIERS (Feb 26, 2026)
// =====================================================================
// Pure functions that take `notes` + a fix payload and return the updated
// `notes` text. Used by the interactive ConflictModal so providers can
// one-tap-fix a detected conflict without leaving the modal.
// =====================================================================

// Rewrite all pronouns in `notes` to the chosen gender. The replacements
// preserve case (He → She when the original was capitalized).
const PRONOUN_MAPS = {
  male: { she: 'he', her: 'him', hers: 'his', herself: 'himself' },
  female: { he: 'she', him: 'her', his: 'her', himself: 'herself' },
};
// Possessive "his" → "her" can be ambiguous (subject vs object) but for
// in-clinic notes ("his BP", "her BP") the simple swap is correct >95% of
// the time. The provider can hand-edit anything wrong before generate.

const PRONOUN_RE_CACHE = {};
const getPronounRe = (word) => {
  if (PRONOUN_RE_CACHE[word]) return PRONOUN_RE_CACHE[word];
  const re = new RegExp(`\\b${word}\\b`, 'gi');
  PRONOUN_RE_CACHE[word] = re;
  return re;
};

const preserveCase = (orig, replacement) => {
  if (!orig) return replacement;
  if (orig[0] === orig[0].toUpperCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1);
  }
  return replacement;
};

/** Rewrite the notes so pronouns match the chosen gender. */
export const applyPronounFix = (notes, gender) => {
  const map = PRONOUN_MAPS[gender];
  if (!map || !notes) return notes;
  let out = notes;
  for (const [wrong, right] of Object.entries(map)) {
    out = out.replace(getPronounRe(wrong), (m) => preserveCase(m, right));
  }
  return out;
};

/**
 * Remove sentences that contradict the chosen "keep" decision for a symptom.
 *   keep='positive' → strip the "denies <sym>" clause
 *   keep='negative' → strip the "endorses/reports/c/o <sym>" clause
 *
 * We only strip the SYMPTOM TOKEN from the unwanted clause, not the whole
 * sentence — that way other content in the sentence ("denies cough, fever,
 * SOB" → "denies fever, SOB") survives.
 */
export const applyEndorseDeniesFix = (notes, { symptom, keep }) => {
  if (!notes || !symptom || !keep) return notes;
  const targetRe = keep === 'positive'
    ? /\b(denies|denied|denying|negative for)\b/i
    : /\b(endorses|endorse|reports|reporting|complains? of|c\/o|states)\b/i;

  const sentences = notes.split(/(?<=[.?!])\s+|\n+/);
  const escSym = symptom.replace(/\s+/g, '\\s+').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const updated = sentences.map((sent) => {
    if (!targetRe.test(sent)) return sent;
    // Remove the symptom token (and surrounding comma/conjunction)
    const re1 = new RegExp(`\\s*,\\s*${escSym}\\b`, 'i'); // ", cough"
    const re2 = new RegExp(`\\b${escSym}\\b\\s*,\\s*`, 'i'); // "cough, "
    const re3 = new RegExp(`\\b${escSym}\\b\\s*(and|or)?\\s*`, 'i'); // bare
    let next = sent.replace(re1, '').replace(re2, '');
    if (new RegExp(`\\b${escSym}\\b`, 'i').test(next)) {
      next = next.replace(re3, '');
    }
    return next.trim();
  }).filter((s) => s && s.length > 1);
  return updated.join(' ');
};

export default scanNotesForConflicts;
